// Adapts a versioned meetstandaard document into the shape the effect picker
// already consumes, so AddEffectFlow / EffectDatabaseItemsList need no rewrite.
//
// Two catalogue formats are live at once:
//   - legacy  /database                       → categories → effects → questions
//   - versioned /meetstandaard/{sector}/{ver} → meta + effecten → stellingen
// The legacy one is being retired once arbeidsparticipatie and gelijke-kansen
// move into the versioned pipeline; until then both feed the same picker.
//
// AI-CONTEXT: See docs/pitfalls.md#api-meetstandaard-same-name-sector-variants

/**
 * Fallback MSIId for an adopted effect, for documents that predate `uid`.
 *
 * NOT the bare `EFF-01`: that numbering restarts in every standaard, and slugs
 * repeat across sectors ("fysieke-gezondheid" exists in gelijke-kansen too).
 * MSIId is a cross-tenant benchmark key, so a collision would silently merge
 * two different effects' answers. Sector + slug is unique and stays readable.
 *
 * Since 2026-08-17 the documents publish a `uid` in exactly this format, so the
 * two agree today. `effectUidOf` prefers theirs — see there for why.
 *
 * @param {string} sector - meta.sector, e.g. 'energiearmoede'
 * @param {string} slug - effect slug, e.g. 'fysieke-gezondheid'
 * @returns {string}
 */
const msiIdFor = (sector, slug) => `${sector}:${slug}`

/**
 * The effect's cross-tenant key: the document's own `uid` when it has one,
 * otherwise our composition.
 *
 * Reading theirs rather than rebuilding it means their definition wins if the
 * format ever changes — ours becomes the fallback, not a competing source of
 * truth. The fallback still matters: 0.9 shipped without `uid` and was patched
 * in place, so anything cached before that patch, and every older pinned
 * document, carries none.
 *
 * @param {object} effect - one entry of `doc.effecten`
 * @param {string} sector - meta.sector
 * @returns {string}
 */
const effectUidOf = (effect, sector) => effect?.uid || msiIdFor(sector, effect?.slug)

/**
 * @typedef {object} AdaptedQuestion
 * @property {string} id - Stable, namespaced per effect
 * @property {string} name - The statement text
 * @property {'positive'|'negative'} posNeg
 * @property {number} stellingNummer - Position within its effect, valid as an identifier only because StandaardVersion pins the document it indexes into
 * @property {'all'} targetGroupsMode - The versioned documents carry no target-group split yet
 * @property {Array<string>} targetGroups
 *
 * @typedef {object} AdaptedEffect
 * @property {string} id - MSIId
 * @property {string} slug
 * @property {string} name
 * @property {string} description
 * @property {string} category
 * @property {Array<string>} sectors
 * @property {string} standaard
 * @property {string} standaardVersion
 * @property {Array<AdaptedQuestion>} questions
 * @property {Array<{stelling: string, reason: string}>} skippedQuestions
 * @property {Array<{score: number, monetaryValue: number}>} scores
 */

/**
 * Only the explicit boolean decides direction. `richting` sits next to it but
 * is free text with 19 spellings across 52 statements, so it is never parsed.
 * `null` means genuinely unknown: refuse to score rather than assume a
 * direction, since guessing wrong silently inverts the statement.
 *
 * @param {*} negatiefGeformuleerd
 * @returns {'positive'|'negative'|null}
 */
const directionOf = (negatiefGeformuleerd) => {
    if (negatiefGeformuleerd === true) return 'negative'
    if (negatiefGeformuleerd === false) return 'positive'
    return null
}

/**
 * Monetary situation values per level, in the shape the monetisation helpers
 * already expect from the legacy catalogue.
 *
 * These are the document's own per-level totals, which it flags as indicative
 * and NOT safe to sum across effects without the overlap correction from
 * `aggregatie` — that correction lives in applyOverlapCorrection, not here.
 *
 * @param {object|null} monetarisering
 * @returns {Array<{score: number, monetaryValue: number}>}
 */
const scoresFrom = (monetarisering) => {
    const niveaus = Array.isArray(monetarisering?.niveaus) ? monetarisering.niveaus : []
    return niveaus
        // A level the source could not price is null, and Number(null) is 0:
        // without this filter that level would read as a real €0.
        .filter((niveau) => niveau?.totaleWaardeIndicatief != null && niveau.totaleWaardeIndicatief !== '')
        .map((niveau) => ({
            score: Number(niveau?.score),
            monetaryValue: Number(niveau?.totaleWaardeIndicatief),
        }))
        .filter((s) => Number.isFinite(s.score) && Number.isFinite(s.monetaryValue))
        .sort((a, b) => a.score - b.score)
}

/**
 * @param {object|null|undefined} doc - A versioned meetstandaard document
 * @returns {{sector: string, version: string, effects: Array<AdaptedEffect>, skippedEffects: Array<{slug: string, reason: string}>}}
 */
const adaptMeetstandaardDocument = (doc) => {
    const meta = doc?.meta || {}
    const sector = meta.sector || ''
    const version = meta.version || ''
    const effecten = Array.isArray(doc?.effecten) ? doc.effecten : []

    /** @type {Array<AdaptedEffect>} */
    const effects = []
    /** @type {Array<{slug: string, reason: string}>} */
    const skippedEffects = []

    effecten.forEach((effect) => {
        const slug = effect?.slug
        if (!slug) return

        const stellingen = Array.isArray(effect.stellingen) ? effect.stellingen : []

        // An effect the standaard defines but does not measure (co2-reductie,
        // arbeidsparticipatie) cannot become a matrix questionnaire.
        if (stellingen.length === 0) {
            skippedEffects.push({ slug, reason: 'no-statements' })
            return
        }

        const id = effectUidOf(effect, sector)
        const questions = []
        const skippedQuestions = []

        stellingen.forEach((stelling) => {
            const posNeg = directionOf(stelling?.negatiefGeformuleerd)
            if (!posNeg) {
                // Kept visible rather than dropped silently — an unscoreable
                // statement is a data problem someone should see.
                skippedQuestions.push({ stelling: stelling?.stelling || '', reason: 'unknown-direction' })
                return
            }
            // nummer is half the row's identity and ends up in a Firestore
            // payload, which rejects undefined outright. Every 0.9 statement
            // has one; a document without it is malformed, not scoreable.
            if (!Number.isFinite(stelling?.nummer)) {
                skippedQuestions.push({ stelling: stelling?.stelling || '', reason: 'no-number' })
                return
            }
            questions.push({
                id: `${id}#${stelling.nummer}`,
                name: stelling.stelling,
                posNeg,
                // Stored as its own field on the row, not parsed back out of
                // `id` — Firestore cannot query a substring.
                stellingNummer: stelling.nummer,
                targetGroupsMode: 'all',
                targetGroups: [],
            })
        })

        effects.push({
            id,
            slug,
            name: effect.effect || '',
            description: effect.definitie || '',
            category: effect.categorie || '',
            sectors: [sector],
            standaard: sector,
            standaardVersion: version,
            questions,
            skippedQuestions,
            scores: scoresFrom(effect.monetarisering),
        })
    })

    return { sector, version, effects, skippedEffects }
}

export default adaptMeetstandaardDocument
export { msiIdFor, effectUidOf, directionOf, scoresFrom }
